import {EventEmitter} from './event-emitter';
import {resolveDriver} from './drivers';
import {attachRenderMethods} from './render';
import type {
  BridgeComponent,
  BridgeComponentConstructor,
  BridgeComponentOptions,
  EventEmitterType
} from './types';

const driverPackageMap: Record<string, string> = {
  react: '@monei-js/react-components',
  vue3: '@monei-js/vue-components',
  angular: '@monei-js/angular-components'
};

function warnDriverDeprecation(name: string): void {
  const pkg = driverPackageMap[name];
  const replacement = pkg ? `Use ${pkg} instead.` : '';
  console.warn(`[MONEI] .driver('${name}') is deprecated. ${replacement}`.trim());
}

/**
 * Normalize aliased prop names to canonical names.
 * Aliases define alternative INPUT names: { style: 'innerStyle' } means
 * merchants can pass either `style` or `innerStyle`, but internally
 * the canonical name `style` is always used (matching zoid semantics).
 */
export function normalizeAliases(
  props: Record<string, any>,
  aliases?: Record<string, string>
): Record<string, any> {
  if (!aliases) return props;

  // Build reverse map: alias value → canonical key
  const reverseMap = new Map<string, string>();
  for (const [canonical, alias] of Object.entries(aliases)) {
    reverseMap.set(alias, canonical);
  }

  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(props)) {
    const canonicalKey = reverseMap.get(key) ?? key;
    result[canonicalKey] = value;
  }
  return result;
}

/**
 * Creates a bridge component constructor (replaces zoid.create).
 *
 * Returns a class that can be instantiated with `new Component(props)`
 * and has a static `.driver()` method for framework wrappers.
 */
export function createComponent<P extends Record<string, any>>(
  options: BridgeComponentOptions<P>
): BridgeComponentConstructor<P> {
  class Component implements BridgeComponent<P> {
    props: P;
    state: Record<string, any> = {};
    event: EventEmitterType;
    submit?: (options?: any) => Promise<any>;

    constructor(props: P) {
      this.event = new EventEmitter();

      // Normalize aliased prop names to canonical (e.g., innerStyle → style)
      const normalizedProps = normalizeAliases(props as Record<string, any>, options.aliases) as P;

      // Apply default props
      if (options.defaultProps) {
        const defaults = options.defaultProps({event: this.event});
        this.props = {...defaults, ...normalizedProps} as P;
      } else {
        this.props = {...normalizedProps};
      }

      // Validate
      if (options.validate) {
        options.validate({props: this.props});
      }

      // Wire render/destroy/close methods (Task 2)
      attachRenderMethods(this, options);

      // Wire submit() if provided by component options (e.g., CardInput)
      if (options.submit) {
        this.submit = options.submit(this);
      }
    }

    // Render — wired by attachRenderMethods in constructor
    async render(_container?: string | HTMLElement): Promise<void> {
      throw new Error('render() not attached');
    }

    // UpdateProps — wired by attachRenderMethods
    async updateProps(_props: Partial<P>): Promise<void> {
      throw new Error('updateProps() not attached — call render() first');
    }

    async close(): Promise<void> {
      throw new Error('close() not attached');
    }

    async destroy(): Promise<void> {
      throw new Error('destroy() not attached');
    }

    driver(name: string, deps?: Record<string, any>): any {
      warnDriverDeprecation(name);
      return resolveDriver(name, Component as unknown as BridgeComponentConstructor<P>, deps);
    }
  }

  // Wrapper that supports both `new Component(props)` and `Component(props)`
  function ComponentWrapper(this: any, props: P) {
    if (!(this instanceof ComponentWrapper)) {
      return new (ComponentWrapper as any)(props);
    }
    return new Component(props);
  }

  // Copy prototype so instanceof checks work
  ComponentWrapper.prototype = Component.prototype;

  // Static driver method
  ComponentWrapper.driver = function (name: string, deps?: Record<string, any>): any {
    warnDriverDeprecation(name);
    return resolveDriver(name, ComponentWrapper as unknown as BridgeComponentConstructor<P>, deps);
  };

  // Store options on prototype for later tasks to access
  (Component.prototype as any).__options = options;

  return ComponentWrapper as unknown as BridgeComponentConstructor<P>;
}
